import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import {
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  IconButton,
  OutlinedInput,
  Select as SelectField,
  MenuItem,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { TimePicker } from "antd";
import dayjs from "dayjs";

import {
  DATE_TIME_FORMAT,
  DEPARTMENT_IN_CHANGE,
  LOCALE_JP,
} from "@bms/constants";
import { DatePickerIcon } from "@bms/assets";
import useRequestContent, { Props, ReceivedProps } from "./hook";
import { Styles } from "./styled";
import { isEmpty } from "lodash";
import { formatDay } from "@bms/utility";
import { ModalConfirm } from "@bms/components";

const RequestContentLayout: FC<Props> = ({
  register,
  setValue,
  getValues,
  handleOptions,
  watch,
  trigger,
  diffTime,
  setRequestOrderDate,
  setReturnDate,
  setRequestStartDate,
  setRequestEndDate,
  clearErrors,
  totalWorkingHours,
  errors,
  control,
  isView,
  subcontractorOptions,
  workerOptions,
  isWorkerLoading,
  setIsReturnDate,
  isReturnDate,
  setRequestReturnStartDate,
  setRequestReturnEndDate,
  openConfirmDialog,
  setOpenConfirmDialog,
  isReturnDateUser,
  pendingReturnDate,
  setPendingReturnDate,
  returnDate,
  handleTimePickerClick,
  allowTimePicker,
  pendingField,
  setAllowTimePicker,
  setPendingField,
  isConfirmUpdate,
  setIsConfirmUpdate,
  setIsChangeTime,
}) => {
  const clearWorker = () => {
    for (let i = 0; i < 8; i++) {
      setValue(`worker_id_${i}`, "");
    }
  };

  const prefix = isReturnDate ? "return_" : "";
  const fieldStartWorking = `${prefix}start_working_hour`;
  const fieldEndWorking = `${prefix}end_working_hour`;

  return (
    <Styles className="request-content">
      <div className="main">
        <div className="field-wrapper">
          <div className="input-field">
            <Controller
              name="request_order_date"
              control={control}
              defaultValue={null}
              render={() => (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={LOCALE_JP}
                >
                  <FormControl variant="outlined">
                    <InputLabel>訪問日</InputLabel>
                    <MobileDatePicker
                      {...register("request_order_date")}
                      onClose={() => {
                        setValue("request_order_date", null);
                        clearWorker();
                      }}
                      onAccept={(e) => {
                        setValue("request_order_date", e);
                        clearWorker();
                        if (!e) return;
                        setRequestOrderDate(dayjs(e).format("YYYY-MM-DD"));
                        setIsReturnDate(false);
                        clearErrors("request_order_date");
                      }}
                      label="訪問日"
                      disabled={isView}
                      value={getValues("request_order_date")}
                      inputFormat={
                        DATE_TIME_FORMAT.DATE_JP +
                        `(${formatDay(getValues("request_order_date"))})`
                      }
                      onChange={(e) => setValue("request_order_date", e)}
                      components={{
                        ActionBar: ({ onCancel, onAccept }: any) => (
                          <div className="MuiDialogActions-root">
                            <button onClick={onCancel}>クリア</button>
                            <button onClick={onAccept}>OK</button>
                          </div>
                        ),
                      }}
                      renderInput={(params: any) => (
                        <OutlinedInput
                          {...params}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton edge="end">
                                <img src={DatePickerIcon} />
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                      )}
                    />
                  </FormControl>
                </LocalizationProvider>
              )}
            />
            {errors?.request_order_date && (
              <p className="error">{errors.request_order_date?.message}</p>
            )}
          </div>

          <div
            className="input-field"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <span>+</span>
              <Controller
                name="return_order_date"
                control={control}
                defaultValue={null}
                render={() => (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={LOCALE_JP}
                  >
                    <FormControl variant="outlined">
                      <InputLabel>再訪問日</InputLabel>
                      <MobileDatePicker
                        {...register("return_order_date")}
                        onAccept={(e) => {
                          if (isReturnDateUser && !isConfirmUpdate) {
                            setPendingReturnDate(e);
                            setOpenConfirmDialog(true);
                          } else {
                            setValue("return_order_date", e);
                            setIsReturnDate(!!e);
                            setReturnDate(dayjs(e).format("YYYY-MM-DD"));
                            setIsChangeTime(true);
                          }
                        }}
                        label="再訪問日"
                        disabled={!watch("request_content_id") || isView}
                        value={getValues("return_order_date")}
                        inputFormat={
                          DATE_TIME_FORMAT.DATE_JP +
                          `(${formatDay(getValues("return_order_date"))})`
                        }
                        onChange={(e) => setValue("return_order_date", e)}
                        components={{
                          ActionBar: ({ onCancel, onAccept }: any) => (
                            <div className="MuiDialogActions-root">
                              <button
                                onClick={() => {
                                  setValue("return_order_date", null);

                                  if (isReturnDate) {
                                    setIsReturnDate(true);
                                  } else {
                                    setIsReturnDate(false);
                                  }

                                  onCancel();
                                }}
                              >
                                クリア
                              </button>
                              <button onClick={onAccept}>OK</button>
                            </div>
                          ),
                        }}
                        renderInput={(params: any) => (
                          <OutlinedInput
                            {...params}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton edge="end">
                                  <img src={DatePickerIcon} />
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </LocalizationProvider>
                )}
              />
            </div>
            {errors?.return_order_date && (
              <p
                className="error"
                style={{
                  paddingLeft: "12px",
                }}
              >
                {errors.return_order_date?.message}
              </p>
            )}
          </div>
        </div>
      </div>

      <ModalConfirm
        isOpen={openConfirmDialog}
        isSubmitting={false}
        height={270}
        content="一部の作業員は既に異なる日時を設定しています。新しい日時を設定すると、作業員の日時が上書きされます"
        onOk={() =>
          Promise.resolve().then(() => {
            if (pendingReturnDate) {
              setValue("return_order_date", pendingReturnDate);
              setIsReturnDate(true);
              setReturnDate(dayjs(pendingReturnDate).format("YYYY-MM-DD"));
              setPendingReturnDate(null);
              setIsChangeTime(true);
            }
            setAllowTimePicker(true);
            setOpenConfirmDialog(false);
            setIsConfirmUpdate(true);
          })
        }
        onClose={() => {
          setValue("return_order_date", returnDate);
          setPendingReturnDate(null);
          setOpenConfirmDialog(false);
        }}
      />

      <h4 className="title">作業予定時間</h4>
      <div className="main">
        <div className="field-wrapper">
          <div>
            <div className="input-field">
              <InputLabel>開始</InputLabel>
              <Controller
                name={fieldStartWorking}
                control={control}
                defaultValue={null}
                render={(props) => {
                  const isAllow =
                    fieldStartWorking === pendingField && allowTimePicker;
                  return (
                    <TimePicker
                      {...props}
                      disabled={isView}
                      clearIcon={<></>}
                      name={fieldStartWorking}
                      format={DATE_TIME_FORMAT.TIME}
                      className={"timepicker-start"}
                      value={
                        getValues(fieldStartWorking)
                          ? dayjs(
                              getValues(fieldStartWorking),
                              DATE_TIME_FORMAT.TIME
                            )
                          : null
                      }
                      placeholder=""
                      suffixIcon={<></>}
                      showNow={false}
                      onChange={(_, timeString: any) => {
                        if (!isConfirmUpdate && !isAllow) return false;
                        setValue(fieldStartWorking, timeString);
                        trigger(fieldStartWorking);
                        if (isReturnDate) {
                          setRequestReturnStartDate(timeString || "");
                        } else {
                          setRequestStartDate(timeString || "");
                        }
                        setIsChangeTime(true);
                      }}
                      onClick={() =>
                        handleTimePickerClick(
                          fieldStartWorking,
                          isReturnDateUser
                        )
                      }
                      open={isAllow ? true : false}
                      onOpenChange={(open) => {
                        if (!open) {
                          setAllowTimePicker(false);
                          setPendingField(null);
                        }
                      }}
                    />
                  );
                }}
              />
            </div>
            {errors?.[fieldStartWorking] && (
              <p className="error">{errors[fieldStartWorking]?.message}</p>
            )}
          </div>
          <div>
            <div className="input-field">
              <InputLabel>終了</InputLabel>
              <Controller
                name={fieldEndWorking}
                control={control}
                defaultValue={null}
                render={(props) => {
                  const isAllow =
                    fieldEndWorking === pendingField && allowTimePicker;
                  return (
                    <TimePicker
                      {...props}
                      disabled={isView}
                      clearIcon={<></>}
                      name={fieldEndWorking}
                      format={DATE_TIME_FORMAT.TIME}
                      className={"timepicker-end"}
                      value={
                        getValues(fieldEndWorking)
                          ? dayjs(
                              getValues(fieldEndWorking),
                              DATE_TIME_FORMAT.TIME
                            )
                          : null
                      }
                      placeholder=""
                      suffixIcon={<></>}
                      showNow={false}
                      onChange={(_, timeString: any) => {
                        if (!isConfirmUpdate && !isAllow) return false;
                        if (isReturnDate) {
                          setRequestReturnEndDate(timeString || "");
                        } else {
                          setRequestEndDate(timeString || "");
                        }
                        setValue(fieldEndWorking, timeString);
                        trigger(fieldEndWorking);
                        setIsChangeTime(true);
                      }}
                      onClick={() =>
                        handleTimePickerClick(fieldEndWorking, isReturnDateUser)
                      }
                      open={isAllow ? true : false}
                      onOpenChange={(open) => {
                        if (!open) {
                          setAllowTimePicker(false);
                          setPendingField(null);
                        }
                      }}
                    />
                  );
                }}
              />
            </div>
            {errors?.[fieldEndWorking] && (
              <p className="error">{errors[fieldEndWorking]?.message}</p>
            )}
          </div>
        </div>
        {(() => {
          for (let item = 0; item < 8; item++) {
            const department_in_charge =
              watch(`department_in_charge_${item}`) ||
              getValues(`department_in_charge_${item}`);
            const work_id =
              watch(`worker_id_${item}`) || getValues(`worker_id_${item}`);

            if (
              department_in_charge &&
              work_id &&
              getValues(fieldEndWorking) &&
              getValues(fieldStartWorking)
            ) {
              const existOption = workerOptions.filter((idx) => {
                return (
                  idx.department_in_charge == department_in_charge &&
                  parseInt(idx.value) == parseInt(work_id)
                );
              });

              if (!isWorkerLoading && existOption.length === 0) {
                return (
                  <p className="error" key={item}>
                    サービス追加できません。作業予定時間か作業員を変更してください。
                  </p>
                );
              }
            }
          }
          return null;
        })()}
      </div>

      <h4 className="title">担当作業員</h4>
      <div className="main">
        <div
          className="field-wrapper"
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "6px 0",
          }}
        >
          {Array.from(Array(8).keys()).map((item) => (
            <div
              key={item}
              style={{
                width: "47%",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span className="index">{item + 1}</span>
              <div
                className="item-wrapper"
                style={{
                  marginRight: "6px",
                }}
              >
                <FormControl variant="outlined">
                  <InputLabel>部署名</InputLabel>
                  <SelectField
                    sx={{
                      "& .MuiSelect-icon": {
                        display: watch(`department_in_charge_${item}`)
                          ? "none"
                          : "",
                      },
                    }}
                    {...(!isView && {
                      endAdornment: (
                        <ClearIcon
                          sx={{
                            visibility: watch(`department_in_charge_${item}`)
                              ? "visible"
                              : "hidden",
                          }}
                          className="clearable"
                          onClick={() => {
                            setValue(`department_in_charge_${item}`, "");
                            setValue(`worker_id_${item}`, "");
                          }}
                        />
                      ),
                    })}
                    onChange={(e) => {
                      setValue(`department_in_charge_${item}`, e.target.value);
                    }}
                    value={watch(`department_in_charge_${item}`) || ""}
                    // {...register(`department_in_charge_${item}`)}
                    name={`department_in_charge_${item}`}
                    variant="standard"
                    defaultValue={getValues(`department_in_charge_${item}`)}
                    label="部署名"
                    disabled={isView}
                  >
                    <MenuItem value="third_houses">
                      {DEPARTMENT_IN_CHANGE["third_houses"]}
                    </MenuItem>
                    <MenuItem value="oyakudachiya">
                      {DEPARTMENT_IN_CHANGE["oyakudachiya"]}
                    </MenuItem>
                    <MenuItem value="consignment" disabled>
                      {DEPARTMENT_IN_CHANGE["consignment"]}
                    </MenuItem>
                  </SelectField>
                </FormControl>
              </div>
              <div className="item-wrapper">
                <FormControl variant="outlined">
                  <InputLabel>作業員名</InputLabel>
                  <SelectField
                    sx={{
                      "& .MuiSelect-icon": {
                        display: watch(`worker_id_${item}`) ? "none" : "",
                      },
                    }}
                    {...(!isView && {
                      endAdornment: (
                        <ClearIcon
                          sx={{
                            visibility: watch(`worker_id_${item}`)
                              ? "visible"
                              : "hidden",
                          }}
                          className="clearable"
                          onClick={() => setValue(`worker_id_${item}`, "")}
                        />
                      ),
                    })}
                    onChange={(e) => {
                      setValue(`worker_id_${item}`, e.target.value);
                    }}
                    // {...register(`worker_id_${item}`)}
                    name={`worker_id_${item}`}
                    value={watch(`worker_id_${item}`) || ""}
                    variant="standard"
                    defaultValue={getValues(`worker_id_${item}`)}
                    label="作業員名"
                    disabled={
                      isView ||
                      // !watch("request_order_date") ||
                      !watch(`department_in_charge_${item}`)
                    }
                  >
                    {handleOptions(
                      watch(`department_in_charge_${item}`) ||
                        getValues(`department_in_charge_${item}`)
                    ).map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </SelectField>
                </FormControl>
              </div>
            </div>
          ))}
        </div>

        <div className="field-wrapper">
          {Array.from(Array(4).keys()).map((item) => (
            <div key={item} className="item-wrapper">
              <FormControl variant="outlined">
                <InputLabel>外注</InputLabel>
                <SelectField
                  sx={{
                    "& .MuiSelect-icon": {
                      display: watch(`subcontractor_id_${item}`) ? "none" : "",
                    },
                  }}
                  {...(!isView && {
                    endAdornment: (
                      <ClearIcon
                        sx={{
                          visibility: watch(`subcontractor_id_${item}`)
                            ? "visible"
                            : "hidden",
                        }}
                        className="clearable"
                        onClick={() => {
                          setValue(`subcontractor_id_${item}`, "");
                        }}
                      />
                    ),
                  })}
                  displayEmpty
                  onChange={(e) => {
                    setValue(`subcontractor_id_${item}`, e.target.value);
                  }}
                  // {...register(`subcontractor_id_${item}`)}
                  value={watch(`subcontractor_id_${item}`) || ""}
                  name={`subcontractor_id_${item}`}
                  variant="standard"
                  defaultValue={getValues(`subcontractor_id_${item}`)}
                  label="外注"
                  disabled={isView}
                >
                  {subcontractorOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </SelectField>
              </FormControl>
            </div>
          ))}
        </div>
      </div>

      <div className="main">
        <div className="input-field">
          <InputLabel className="title">
            相談依頼内容 <span>(報告書・簡易報告書に記載)</span>
          </InputLabel>
          <TextField
            className="edit-content-item"
            // inputRef={(input) => input && input.focus()}
            onFocus={(e) => {
              const textarea = e.currentTarget;
              e.currentTarget.setSelectionRange(
                e.currentTarget.value.length,
                e.currentTarget.value.length
              );
              if (textarea.selectionStart == textarea.selectionEnd) {
                textarea.scrollTop = textarea.scrollHeight + 10;
              }
            }}
            multiline
            rows={4}
            {...register("content")}
            name="content"
            // autoFocus
            disabled={isView}
            placeholder=""
          />
          {errors?.content && (
            <p className="error">{errors.content?.message}</p>
          )}
        </div>
      </div>

      <div className="main">
        <div className="input-field">
          <InputLabel className="title">
            工事件名 <span>(報告書／見積書／請求書に記載)</span>
          </InputLabel>
          <TextField
            {...register("contruction_subject")}
            name="contruction_subject"
            placeholder=""
            disabled={isView}
          />
          {errors?.contruction_subject && (
            <p className="error">{errors.contruction_subject?.message}</p>
          )}
        </div>
      </div>

      <div className="main">
        {!isEmpty(getValues("return_date_histories")) ? (
          <>
            <div className="return-order-date">
              <div className="field-wrapper">
                <div className="input-field">
                  <TextField
                    {...(getValues("actual_start_time") &&
                      getValues("actual_end_time") && {
                        value: `${
                          diffTime(
                            getValues("actual_start_time"),
                            getValues("actual_end_time")
                          ).hours
                        }時間${
                          diffTime(
                            getValues("actual_start_time"),
                            getValues("actual_end_time")
                          ).minutes
                        }分`,
                      })}
                    placeholder="作業時間"
                    disabled
                  />
                </div>
              </div>
              {!isEmpty(getValues("return_date_histories")) ? (
                getValues("return_date_histories")
                  ?.filter(
                    (item: any) =>
                      item?.actual_start_time && item?.actual_end_time
                  )
                  ?.map((item: any, index: number) => (
                    <div className="field-wrapper" key={index}>
                      <div className="input-field">
                        <TextField
                          value={`${
                            diffTime(
                              item?.actual_start_time,
                              item?.actual_end_time
                            ).hours
                          }時間${
                            diffTime(
                              item?.actual_start_time,
                              item?.actual_end_time
                            ).minutes
                          }分`}
                          placeholder="作業時間"
                          disabled
                        />
                      </div>
                    </div>
                  ))
              ) : (
                <div className="field-wrapper">
                  <div className="input-field">
                    <TextField placeholder="作業時間" disabled />
                  </div>
                </div>
              )}
            </div>
            <div className="field-wrapper">
              <div className="input-field">
                <TextField
                  value={totalWorkingHours}
                  placeholder="合計時間"
                  disabled
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="return-order-date">
              <div className="field-wrapper">
                <div className="input-field">
                  <TextField
                    {...(getValues("actual_start_time") &&
                      getValues("actual_end_time") && {
                        value: `${
                          diffTime(
                            getValues("actual_start_time"),
                            getValues("actual_end_time")
                          ).hours
                        }時間${
                          diffTime(
                            getValues("actual_start_time"),
                            getValues("actual_end_time")
                          ).minutes
                        }分`,
                      })}
                    placeholder="作業時間"
                    disabled
                  />
                </div>
              </div>
              <div className="field-wrapper">
                <div className="input-field">
                  <TextField placeholder="作業時間" disabled />
                </div>
              </div>
            </div>
            <div className="field-wrapper">
              <div className="input-field">
                <TextField
                  {...(getValues("actual_start_time") &&
                    getValues("actual_end_time") && {
                      value: `${
                        diffTime(
                          getValues("actual_start_time"),
                          getValues("actual_end_time")
                        ).hours
                      }時間${
                        diffTime(
                          getValues("actual_start_time"),
                          getValues("actual_end_time")
                        ).minutes
                      }分`,
                    })}
                  placeholder="合計時間"
                  disabled
                />
              </div>
            </div>
          </>
        )}
      </div>
    </Styles>
  );
};

const RequestContent: FC<ReceivedProps> = (props) => (
  <RequestContentLayout {...useRequestContent(props)} />
);

export default RequestContent;
